import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { useForm } from "react-hook-form";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { toast } from "react-toastify";
import moment from "moment";
import { safeText } from "../utils/hubHelpers";

export default function QuickServiceModal({ isOpen, toggle, machine, onSuccess }) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchVendors();
    }
  }, [isOpen]);

  const fetchVendors = async () => {
    try {
      const res = await callApi(apiUrl.get_vendor_list, { method: "GET" });
      if (res?.status === true) {
        const list = Array.isArray(res?.data?.vendor)
          ? res.data.vendor
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setVendors(list);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const onSubmit = async (data) => {
    if (!machine?.id) return;
    setLoading(true);
    try {
      const payload = {
        machineryId: machine.id,
        serviceDate: data.serviceDate || moment().format("YYYY-MM-DD"),
        serviceType: data.serviceType,
        cost: data.cost || 0,
        partsReplaced: data.partsReplaced || "",
        vendorId: data.vendorId || null,
        nextServiceDueAt: data.nextServiceDueAt || null,
        nextServiceDate: data.nextServiceDate || null,
        remarks: data.remarks || "",
      };

      const res = await callApi(apiUrl.add_machinery_service, {
        method: "POST",
        data: payload,
      });

      if (res?.status === true) {
        toast.success("Service record added successfully!");
        reset();
        toggle();
        onSuccess?.();
      } else {
        toast.error(res?.message || "Failed to add service record");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          toggle={toggle}
          close={
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={toggle}
              aria-label="Close"
              style={{ filter: "invert(1) grayscale(100%) brightness(200%)", opacity: 0.9, cursor: "pointer" }}
            />
          }
          style={{ background: "#0f172a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          🔧 Log Maintenance / Service: {safeText(machine?.name, "Machine")}
        </ModalHeader>
        <ModalBody>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">
                Service Type *
              </label>
              <select className="form-select" {...register("serviceType", { required: "Service type is required" })}>
                <option value="Periodic Maintenance">Periodic Maintenance (Routine Service)</option>
                <option value="Engine Overhaul">Engine Overhaul</option>
                <option value="Hydraulic Repair">Hydraulic Repair</option>
                <option value="Oil & Filter Replacement">Oil & Filter Replacement</option>
                <option value="Electrical Repair">Electrical Repair</option>
                <option value="Tyre / Track Replacement">Tyre / Track Replacement</option>
                <option value="Breakdown Repair">Breakdown Urgent Repair</option>
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Service Date *
              </label>
              <input
                type="date"
                className="form-control"
                defaultValue={moment().format("YYYY-MM-DD")}
                {...register("serviceDate", { required: "Date is required" })}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Cost (₹)
              </label>
              <input
                type="number"
                step="any"
                className="form-control"
                placeholder="e.g. 15000"
                {...register("cost")}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Service Vendor / Workshop
              </label>
              <select className="form-select" {...register("vendorId")}>
                <option value="">Select Vendor (Optional)</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {safeText(v.firmName || v.name, "Vendor")}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Next Service Date
              </label>
              <input
                type="date"
                className="form-control"
                {...register("nextServiceDate")}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Parts Replaced
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Mobil Oil, Air Filter, Fuel Pipe"
                {...register("partsReplaced")}
              />
            </div>

            <div className="col-12">
              <label className="form-label">
                Service Notes / Mechanic Remarks
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Details of the job done..."
                {...register("remarks")}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={loading} style={{ background: "#2563eb", borderColor: "#2563eb" }}>
            {loading ? "Saving..." : "Save Service Record"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
